import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useParams, useNavigate } from 'react-router-dom';
import { FiCheck, FiClock, FiUser, FiCalendar, FiAlertCircle, FiBookOpen, FiCheckSquare } from 'react-icons/fi';
import { formatDateForDisplay, formatTimeTo12Hour } from '../../utils/datetime-comprehensive';
import { DateTime } from 'luxon';
import apiService from '../../services/api';
import { useAuth } from '../../context/AuthContext';

const Container = styled.div`
  min-height: 100vh;
  background: linear-gradient(180deg, var(--background-secondary, var(--bg-secondary)) 0%, var(--background-primary, var(--bg-primary)) 100%);
  padding: 5rem 1rem 2rem;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  
  @media (max-width: 768px) {
    padding: 1rem 0.75rem;
    align-items: flex-start;
  }

  @media (max-width: 480px) {
    padding: 0.75rem 0.55rem;
  }
`;

const CheckInCard = styled(motion.div)`
  background: var(--background-primary, var(--bg-primary));
  border: 1px solid var(--border-color);
  border-radius: 16px;
  padding: 2rem;
  max-width: 620px;
  width: 100%;
  box-shadow: 0 14px 30px rgba(15, 23, 42, 0.08);
  margin-top: 0;
  
  @media (max-width: 768px) {
    padding: 1.2rem;
    border-radius: 14px;
    max-width: 100%;
    margin-top: 0;
  }

  @media (max-width: 480px) {
    padding: 0.9rem;
    border-radius: 12px;
  }
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 1.35rem;

  @media (max-width: 480px) {
    margin-bottom: 1rem;
  }
`;

const Title = styled.h1`
  font-size: 1.75rem;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0 0 0.4rem 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.65rem;

  svg {
    width: 1.15rem;
    height: 1.15rem;
    color: var(--success-dark, var(--success-color));
  }
  
  @media (max-width: 768px) {
    font-size: 1.6rem;
  }
  
  @media (max-width: 480px) {
    font-size: 1.35rem;
  }
`;

const SessionInfo = styled.div`
  background: var(--background-secondary, var(--bg-secondary));
  border: 1px solid var(--border-color);
  border-radius: 14px;
  padding: 1.2rem;
  margin-bottom: 1.25rem;
  
  @media (max-width: 768px) {
    padding: 0.95rem;
    border-radius: 12px;
  }
`;

const SessionTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0 0 0.8rem 0;

  @media (max-width: 768px) {
    font-size: 1.15rem;
  }

  @media (max-width: 480px) {
    font-size: 1.05rem;
  }
`;

const SessionDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;

  @media (max-width: 480px) {
    gap: 0.6rem;
  }
`;

const SessionDetail = styled.div`
  display: flex;
  align-items: center;
  gap: 0.6rem;
  font-size: 0.95rem;
  color: var(--text-secondary);

  svg {
    color: var(--text-muted);
    flex-shrink: 0;
  }

  @media (max-width: 480px) {
    font-size: 0.88rem;
    line-height: 1.35;
    align-items: flex-start;
  }
`;

const CheckInSection = styled.div`
  margin-bottom: 2rem;

  @media (max-width: 480px) {
    margin-bottom: 1.25rem;
  }
`;

const CheckInButtons = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  flex-wrap: wrap;
  gap: 1rem;

  @media (max-width: 480px) {
    gap: 0.75rem;
  }
`;

const CheckInButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  padding: 0.9rem 1.2rem;
  border: 1px solid var(--border-color);
  border-radius: 12px;
  background: var(--background-primary, var(--bg-primary));
  color: var(--text-primary);
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  min-height: 52px;
  
  &:hover {
    background: var(--background-secondary, var(--bg-secondary));
    border-color: var(--primary-color);
    transform: translateY(-1px);
    box-shadow: 0 8px 18px rgba(15, 23, 42, 0.08);
  }
  
  &.primary {
    background: var(--primary-color);
    color: var(--text-inverse);
    border-color: var(--primary-color);
    
    &:hover {
      background: var(--primary-hover);
    }
  }
  
  &.success {
    background: rgba(104, 211, 145, 0.1);
    color: var(--success-dark);
    border-color: var(--success-color);
    
    &:hover {
      background: rgba(104, 211, 145, 0.2);
    }
  }

  &.successSolid {
    background: #d9f7e6;
    color: #166534;
    border-color: #86efac;
    font-size: 1rem;
    font-weight: 600;
    font-family: inherit;
    line-height: 1.2;

    &:hover {
      background: #c8f0da;
      border-color: #68d391;
    }
  }
  
  &.error {
    background: rgba(252, 129, 129, 0.1);
    color: var(--error-color);
    border-color: var(--error-color);
    
    &:hover {
      background: rgba(252, 129, 129, 0.2);
    }
  }
  
  &:disabled {
    opacity: 0.58;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }

  @media (max-width: 480px) {
    font-size: 0.95rem;
    min-height: 48px;
    padding: 0.78rem 0.9rem;
    gap: 0.5rem;
  }
`;

const StatusMessage = styled(motion.div)`
  padding: 0.9rem 1rem;
  border-radius: 12px;
  font-size: 0.92rem;
  text-align: left;
  margin-bottom: 0.85rem;
  line-height: 1.45;
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;

  svg {
    margin-top: 0.1rem;
    flex-shrink: 0;
  }

  @media (max-width: 480px) {
    font-size: 0.86rem;
    padding: 0.78rem 0.82rem;
  }
  
  &.success {
    background: rgba(104, 211, 145, 0.1);
    border: 1px solid var(--success-color);
    color: var(--success-color);
  }
  
  &.error {
    background: rgba(252, 129, 129, 0.1);
    border: 1px solid var(--error-color);
    color: var(--error-color);
  }
  
  &.info {
    background: rgba(96, 165, 250, 0.1);
    border: 1px solid var(--info-color);
    color: var(--info-color);
  }
`;

const StatusMessageText = styled.span`
  flex: 1;
  min-width: 0;
`;

const LoadingSpinner = styled.div`
  width: 20px;
  height: 20px;
  border: 2px solid transparent;
  border-top: 2px solid currentColor;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 1.5rem 1rem;
  color: var(--text-secondary);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 320px;
`;

const EmptyIcon = styled.div`
  width: 76px;
  height: 76px;
  margin-bottom: 1rem;
  border-radius: 999px;
  display: grid;
  place-items: center;
  background: linear-gradient(180deg, rgba(245, 173, 62, 0.18) 0%, rgba(245, 173, 62, 0.08) 100%);
  color: #B7791F;

  svg {
    width: 36px;
    height: 36px;
  }
`;

const EmptyTitle = styled.h3`
  color: var(--text-primary);
  font-size: 2rem;
  font-weight: 700;
  margin: 0 0 0.75rem 0;

  @media (max-width: 768px) {
    font-size: 1.55rem;
  }
`;

const EmptyDescription = styled.p`
  color: var(--text-secondary);
  font-size: 1.12rem;
  line-height: 1.55;
  margin: 0;
  max-width: 520px;

  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

const TimeWindowInfo = styled.div`
  background: rgba(96, 165, 250, 0.08);
  border: 1px solid rgba(96, 165, 250, 0.3);
  border-radius: 12px;
  padding: 1rem;
  margin: 1rem 0;
`;

const TimeWindowDetail = styled.div`
  display: flex;
  align-items: center;
  gap: 0.55rem;

  @media (max-width: 480px) {
    align-items: flex-start;
    line-height: 1.35;

    svg {
      margin-top: 0.15rem;
      flex-shrink: 0;
    }
  }
`;

const TimeWindowRestricted = styled.div`
  background: rgba(252, 129, 129, 0.1);
  border: 1px solid var(--error-color);
  border-radius: 12px;
  padding: 1rem;
  margin: 1rem 0;
  text-align: left;
`;

const TimeWindowRestrictedMessage = styled.p`
  font-size: 0.92rem;
  color: var(--error-color);
  margin: 0;
  line-height: 1.4;
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;

  svg {
    margin-top: 0.08rem;
    flex-shrink: 0;
  }
`;

const TimeWindowRestrictedMessageText = styled.span`
  flex: 1;
  min-width: 0;
`;

function AttendanceCheckIn() {
  console.log('🔍 AttendanceCheckIn component function called');
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  
  console.log('🔍 AttendanceCheckIn component loaded with sessionId:', sessionId);
  console.log('🔍 Current URL:', window.location.href);
  console.log('🔍 User state:', user);
  console.log('🔍 Auth loading:', authLoading);
  const [nowTick, setNowTick] = useState(Date.now());
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkingIn, setCheckingIn] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [attendanceStatus, setAttendanceStatus] = useState(null);

  // Redirect to login if not authenticated
  useEffect(() => {
    console.log('🔍 AttendanceCheckIn - Auth state:', { user, authLoading, sessionId });
    if (!authLoading && !user) {
      const currentPath = `/attendance/checkin/${sessionId}`;
      console.log('🔍 Redirecting to login with path:', currentPath);
      navigate(`/login?redirect=${encodeURIComponent(currentPath)}`);
    } else if (user) {
      console.log('🔍 User is authenticated, proceeding with check-in');
    }
  }, [user, authLoading, navigate, sessionId]);

  const fetchSessionData = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      
      const response = await apiService.getAttendanceSession(sessionId);
      
      if (response.success) {
        setSession(response.session);
        
        console.log('🔍 Session data loaded:', {
          session: response.session,
          checkinStart: response.session.checkin_start_time,
          checkinEnd: response.session.checkin_end_time,
          hasTimeWindow: !!(response.session.checkin_start_time && response.session.checkin_end_time)
        });
        
        // Check if student is already checked in
        const studentRecord = response.session.records.find(
          record => record.student_id === apiService.getCurrentUser()?.id
        );
        
        if (studentRecord) {
          setAttendanceStatus(studentRecord.status);
        }
        
        // No automatic check-in validation - let the user manually check in when they click the button
      } else {
        setError(response.message || 'Session not found');
      }
    } catch (error) {
      console.error('Error fetching session:', error);
      setError('Failed to load attendance session');
    } finally {
      setLoading(false);
    }
  }, [sessionId]);

  useEffect(() => {
    if (sessionId && user) {
      fetchSessionData();
    }
  }, [sessionId, fetchSessionData, user]);

  useEffect(() => {
    const intervalId = setInterval(() => {
      setNowTick(Date.now());
    }, 1000);

    return () => clearInterval(intervalId);
  }, []);

  const handleCheckIn = async (status = 'present') => {
    try {
      const timeWindowStatus = getTimeWindowStatus();
      if (timeWindowStatus && timeWindowStatus.type === 'before') {
        // Keep the live countdown banner visible (it updates every second).
        setError('');
        setSuccess('');
        return;
      }

      if (timeWindowStatus && timeWindowStatus.type === 'after') {
        setError(timeWindowStatus.message);
        return;
      }

      setCheckingIn(true);
      setError('');
      setSuccess('');
      
      console.log('🔍 Attempting to check in:', { sessionId, status, userId: user?.id });
      
      const response = await apiService.checkInAttendance(sessionId, status);
      
      console.log('🔍 Check-in response:', response);
      console.log('🔍 Check-in response details:', JSON.stringify(response, null, 2));
      
      if (response.success) {
        setSuccess('Successfully checked in for attendance!');
        setAttendanceStatus(status);
        
        // Redirect after successful check-in
        setTimeout(() => {
          navigate('/dashboard');
        }, 2000);
      } else {
        console.error('🔍 Check-in failed:', response);
        console.error('🔍 Check-in failed details:', JSON.stringify(response, null, 2));
        const responseMessage = response.message || 'Failed to check in';
        if (
          responseMessage.includes('Check-in opens in') ||
          responseMessage.includes('Check-in window is available during') ||
          responseMessage.includes('Check-in window has closed') ||
          responseMessage.includes('Check-in is not available at this time')
        ) {
          const liveStatus = getTimeWindowStatus();
          if (liveStatus?.type === 'before') {
            // Keep live countdown panel visible instead of static error text.
            setError('');
            return;
          }

          if (liveStatus?.type === 'after') {
            setError(liveStatus.message);
            return;
          }
        }

        setError(responseMessage);
      }
    } catch (error) {
      console.error('🔍 Error checking in:', error);
      console.error('🔍 Error details:', {
        message: error.message,
        status: error.status,
        response: error.response
      });
      // Show the actual error message from the server with better formatting
      let errorMessage = error.message || 'Failed to check in. Please try again.';
      
      // Temporary debugging - show the raw error message
      console.log('🔍 RAW ERROR MESSAGE:', errorMessage);
      
      console.log('🔍 Error message formatting:', {
        originalMessage: error.message,
        sessionExists: !!session,
        checkinStartTime: session?.checkin_start_time
      });
      
      // If it's a time window error, use the server's formatted message directly
      if (error.message && (
        error.message.includes('Check-in is not available at this time') || 
        error.message.includes('Check-in opens in') ||
        error.message.includes('Check-in window is available during') ||
        error.message.includes('Check-in window has closed') ||
        error.message.includes('Check-in is not available. This session is for')
      )) {
        const liveStatus = getTimeWindowStatus();
        if (liveStatus?.type === 'before') {
          // Keep live countdown panel visible instead of static error text.
          setError('');
          setCheckingIn(false);
          return;
        }

        if (liveStatus?.type === 'after') {
          errorMessage = liveStatus.message;
        } else {
          // Fall back to server message when client-side status can't be derived.
          errorMessage = error.message;
        }

        console.log('🔍 Using formatted time window error message:', errorMessage);
      }
      
      setError(errorMessage);
    } finally {
      setCheckingIn(false);
    }
  };

  const normalizeTimeString = (value) => {
    if (!value) return null;

    // Already HH:mm or HH:mm:ss
    if (/^\d{2}:\d{2}(:\d{2})?$/.test(value)) {
      return value.length === 5 ? `${value}:00` : value;
    }

    // ISO datetime fallback
    const asDate = new Date(value);
    if (!Number.isNaN(asDate.getTime())) {
      return asDate.toLocaleTimeString('en-US', {
        timeZone: 'America/Chicago',
        hour12: false,
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      });
    }

    return null;
  };

  const getCheckinWindowTimes = () => {
    if (!session) {
      return { start: null, end: null };
    }

    // Prefer explicit check-in window, otherwise fall back to session window
    const rawStart = session.checkin_start_time || session.start_time;
    const rawEnd = session.checkin_end_time || session.end_time;

    return {
      start: normalizeTimeString(rawStart),
      end: normalizeTimeString(rawEnd),
      rawStart,
      rawEnd
    };
  };


  const getTimeWindowStatus = () => {
    // Force recomputation every second so countdown text stays live.
    void nowTick;

    if (!session) {
      return null;
    }

    const {
      start: checkinStart,
      end: checkinEnd,
      rawStart,
      rawEnd
    } = getCheckinWindowTimes();

    if (!checkinStart || !checkinEnd) {
      return null;
    }

    const parseTimeToSeconds = (timeValue) => {
      if (!timeValue) return null;
      const normalized = normalizeTimeString(timeValue);
      if (!normalized) return null;
      const [h, m, s] = normalized.split(':').map(Number);
      if ([h, m, s].some(Number.isNaN)) return null;
      return (h * 3600) + (m * 60) + s;
    };

    const getChicagoDateKey = (dateValue) => {
      if (!dateValue) return null;
      if (typeof dateValue === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
        return dateValue;
      }

      const parsed = new Date(dateValue);
      if (Number.isNaN(parsed.getTime())) return null;

      return parsed.toLocaleDateString('en-CA', {
        timeZone: 'America/Chicago',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      });
    };

    const dateKeyToDayIndex = (dateKey) => {
      const ts = Date.parse(`${dateKey}T00:00:00Z`);
      if (Number.isNaN(ts)) return null;
      return Math.floor(ts / 86400000);
    };

    const getDateKeyForBoundary = (rawBoundaryValue, fallbackDateKey) => {
      // Datetime value (ISO/SQL-like) carries its own calendar day.
      if (typeof rawBoundaryValue === 'string' && rawBoundaryValue.includes('T')) {
        const fromDateTime = getChicagoDateKey(rawBoundaryValue);
        if (fromDateTime) return fromDateTime;
      }

      // Time-only values must use the session date fallback.
      return fallbackDateKey || null;
    };

    // Get current time/date in Chicago timezone with second-level precision
    const chicagoNow = DateTime.fromMillis(nowTick).setZone('America/Chicago');
    const currentTime = chicagoNow.toFormat('HH:mm:ss');
    const currentDateKey = chicagoNow.toFormat('yyyy-MM-dd');
    const sessionDateKey = getChicagoDateKey(session?.date || session?.start_time);
    const startDateKey = getDateKeyForBoundary(rawStart, sessionDateKey);
    const endDateKey = getDateKeyForBoundary(rawEnd, startDateKey);

    const formatDurationHms = (totalSeconds) => {
      const safeSeconds = Math.max(0, Math.floor(totalSeconds));
      const hours = Math.floor(safeSeconds / 3600);
      const minutes = Math.floor((safeSeconds % 3600) / 60);
      const seconds = safeSeconds % 60;

      const parts = [];
      if (hours > 0) parts.push(`${hours} hour${hours === 1 ? '' : 's'}`);
      if (minutes > 0 || hours > 0) parts.push(`${minutes} minute${minutes === 1 ? '' : 's'}`);
      parts.push(`${seconds} second${seconds === 1 ? '' : 's'}`);

      if (parts.length === 1) return parts[0];
      if (parts.length === 2) return `${parts[0]} and ${parts[1]}`;
      return `${parts[0]}, ${parts[1]}, and ${parts[2]}`;
    };

    const formatDateLabel = (dateKey) => {
      if (!dateKey) return null;

      const dateObj = new Date(`${dateKey}T12:00:00`);
      if (Number.isNaN(dateObj.getTime())) return null;

      return dateObj.toLocaleDateString('en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric'
      });
    };

    const currentSeconds = parseTimeToSeconds(currentTime);
    const startSeconds = parseTimeToSeconds(checkinStart);
    const endSeconds = parseTimeToSeconds(checkinEnd);
    const currentDayIndex = dateKeyToDayIndex(currentDateKey);
    const startDayIndex = startDateKey ? dateKeyToDayIndex(startDateKey) : null;
    const endDayIndex = endDateKey ? dateKeyToDayIndex(endDateKey) : null;

    if (
      currentSeconds !== null &&
      startSeconds !== null &&
      endSeconds !== null &&
      currentDayIndex !== null &&
      startDayIndex !== null &&
      endDayIndex !== null
    ) {
      const currentAbsolute = (currentDayIndex * 86400) + currentSeconds;
      const startAbsolute = (startDayIndex * 86400) + startSeconds;
      const endAbsolute = (endDayIndex * 86400) + endSeconds;

      if (currentAbsolute < startAbsolute) {
        const secondsUntilStart = startAbsolute - currentAbsolute;
        return {
          type: 'before',
          message: `Check-in opens in ${formatDurationHms(secondsUntilStart)}${formatDateLabel(startDateKey) ? ` (on ${formatDateLabel(startDateKey)} at ${formatTimeTo12Hour(rawStart || checkinStart)} CT)` : ''}`,
          startTime: formatTimeTo12Hour(rawStart || checkinStart),
          endTime: formatTimeTo12Hour(rawEnd || checkinEnd)
        };
      }

      if (currentAbsolute > endAbsolute) {
        const closedDateLabel = formatDateLabel(endDateKey);
        const closedAt = formatTimeTo12Hour(rawEnd || checkinEnd);
        return {
          type: 'after',
          message: closedDateLabel
            ? `Check-in window closed on ${closedDateLabel} at ${closedAt} CT`
            : `Check-in window closed at ${closedAt} CT`,
          startTime: formatTimeTo12Hour(rawStart || checkinStart),
          endTime: formatTimeTo12Hour(rawEnd || checkinEnd)
        };
      }
    } else if (currentTime < checkinStart) {
      // Fallback to time-only comparison if date parsing fails.
      const startTime = new Date(`2000-01-01T${checkinStart}`);
      const currentTimeObj = new Date(`2000-01-01T${currentTime}`);
      const secondsUntilStart = Math.ceil((startTime - currentTimeObj) / 1000);

      return {
        type: 'before',
        message: `Check-in opens in ${formatDurationHms(secondsUntilStart)}${formatDateLabel(startDateKey) ? ` (on ${formatDateLabel(startDateKey)} at ${formatTimeTo12Hour(rawStart || checkinStart)} CT)` : ''}`,
        startTime: formatTimeTo12Hour(rawStart || checkinStart),
        endTime: formatTimeTo12Hour(rawEnd || checkinEnd)
      };
    } else if (currentTime > checkinEnd) {
      // Fallback to time-only comparison if date parsing fails.
      const closedDateLabel = formatDateLabel(endDateKey || currentDateKey);
      const closedAt = formatTimeTo12Hour(rawEnd || checkinEnd);

      return {
        type: 'after',
        message: closedDateLabel
          ? `Check-in window closed on ${closedDateLabel} at ${closedAt} CT`
          : `Check-in window closed at ${closedAt} CT`,
        startTime: formatTimeTo12Hour(rawStart || checkinStart),
        endTime: formatTimeTo12Hour(rawEnd || checkinEnd)
      };
    }

    return {
      type: 'available',
      message: 'Check-in is available now',
      startTime: formatTimeTo12Hour(rawStart || checkinStart),
      endTime: formatTimeTo12Hour(rawEnd || checkinEnd)
    };
  };

  const getStatusMessage = () => {
    if (attendanceStatus === 'present') {
      return 'You have already checked in as present for this session.';
    } else if (attendanceStatus === 'late') {
      return 'You have already checked in as late for this session.';
    }
    // Don't show message for 'absent' status as it's the default
    return null;
  };

  const getStatusColor = () => {
    if (attendanceStatus === 'present') return 'success';
    if (attendanceStatus === 'late') return 'info';
    if (attendanceStatus === 'absent') return 'info'; // Changed from 'error' to 'info'
    return 'info';
  };

  // Don't render anything if not authenticated (will redirect)
  if (!user && !authLoading) {
    console.log('🔍 No user and not loading, returning null');
    return null;
  }

  if (loading || authLoading) {
    return (
      <Container>
        <CheckInCard
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            <LoadingSpinner />
            <p>Loading attendance session...</p>
          </div>
        </CheckInCard>
      </Container>
    );
  }

  if (error && !session) {
    return (
      <Container style={{ alignItems: 'flex-start', paddingTop: '1.25rem' }}>
        <CheckInCard
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          style={{ maxWidth: '860px', padding: '2.25rem 1.75rem' }}
        >
          <EmptyState>
            <EmptyIcon>
              <FiAlertCircle />
            </EmptyIcon>
            <EmptyTitle>Session Not Found</EmptyTitle>
            <EmptyDescription>
              The attendance session you're looking for doesn't exist or you don't have access to it.
            </EmptyDescription>
            <CheckInButton 
              className="primary" 
              onClick={() => navigate('/attendance')}
              style={{ marginTop: '1.8rem', minWidth: '220px' }}
            >
              <FiCheckSquare size={16} />
              Go to My Attendance
            </CheckInButton>
          </EmptyState>
        </CheckInCard>
      </Container>
    );
  }

  return (
    <Container>
      <CheckInCard
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <Header>
          <Title>
            <FiCheckSquare size={24} />
            Attendance Check-In
          </Title>
        </Header>

        {session && (
          <SessionInfo>
            <SessionTitle>{session.title || `${session.course_name} - ${session.course_code}`}</SessionTitle>
            <SessionDetails>
              <SessionDetail>
                <FiCalendar size={16} />
                {formatDateForDisplay(session.date, 'M/d/yyyy')}
              </SessionDetail>
              <SessionDetail>
                <FiUser size={16} />
                {session.instructor_first_name} {session.instructor_last_name}
              </SessionDetail>
              {(session.course_name || session.elective_name || session.course_code) && (
                <SessionDetail>
                  <FiBookOpen size={16} />
                  {session.course_name || session.elective_name || session.course_code}
                </SessionDetail>
              )}
              <SessionDetail>
                <FiClock size={16} />
                {(() => {
                  // Use check-in times if available, otherwise fall back to session times
                  const startTime = session.checkin_start_time || session.start_time;
                  const endTime = session.checkin_end_time || session.end_time;
                  
                  if (startTime && endTime) {
                    return `Check-In Window ${formatTimeTo12Hour(startTime)} - ${formatTimeTo12Hour(endTime)}`;
                  }
                  
                  return 'No time restrictions';
                })()}
              </SessionDetail>
            </SessionDetails>
          </SessionInfo>
        )}

        {session && getTimeWindowStatus() && !error && (
          <>
            {getTimeWindowStatus().type === 'available' ? (
              <TimeWindowInfo>
                <TimeWindowDetail>
                  <FiCheck size={14} />
                  Check In is available now
                </TimeWindowDetail>
              </TimeWindowInfo>
            ) : (
              <TimeWindowRestricted>
                <TimeWindowRestrictedMessage>
                  <FiAlertCircle size={16} />
                  <TimeWindowRestrictedMessageText>{getTimeWindowStatus().message}</TimeWindowRestrictedMessageText>
                </TimeWindowRestrictedMessage>
              </TimeWindowRestricted>
            )}
          </>
        )}

        {error && (
          <StatusMessage className="error" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <FiAlertCircle size={16} />
            <StatusMessageText>{error}</StatusMessageText>
          </StatusMessage>
        )}

        {success && (
          <StatusMessage className="success" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <FiCheck size={16} />
            <StatusMessageText>{success}</StatusMessageText>
          </StatusMessage>
        )}

        {attendanceStatus && getStatusMessage() && !success && (
          <StatusMessage className={getStatusColor()} initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            {getStatusMessage()}
          </StatusMessage>
        )}

        <CheckInSection>
          <CheckInButtons>
            {(!attendanceStatus || attendanceStatus === 'absent') && (
              <CheckInButton 
                className="successSolid"
                onClick={() => handleCheckIn('present')}
                disabled={checkingIn}
                style={{ minWidth: '220px' }}
              >
                {checkingIn ? <LoadingSpinner /> : <FiCheck size={20} />}
                Check In as Present
              </CheckInButton>
            )}

            <CheckInButton 
              className="primary"
              onClick={() => navigate('/attendance')}
              style={{ minWidth: '220px' }}
            >
              Back to My Attendance
            </CheckInButton>
          </CheckInButtons>
        </CheckInSection>
      </CheckInCard>
    </Container>
  );
}

export default AttendanceCheckIn;
